import { P2GBox, P2GButton, P2GContainer, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SidePanelContent } from '~/components/sidepanel-section';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { OnboardingTitle } from '../rules/onboarding-title';
import SubscribePanel, { SubscribeOutcome } from '~/apps/subscriptions/subscribe-panel';
import { findPlanCard, usePlanCards } from '~/apps/subscriptions/use-plan-cards';
import { tierLabel } from '~/apps/subscriptions/types';
import { PendingSubscriptionPlan } from '~/hooks/use-pending-subscription-plan';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';

type props = {
  isXs: boolean;
  pendingPlan: PendingSubscriptionPlan;
  onDone: (outcome: SubscribeOutcome) => void;
};

const SubscribeScreen = ({ pendingPlan, onDone }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  // This screen only ever shows for a plan the customer already picked (off an SEO link), so
  // "Choose your plan" offered a choice that was already made. Name the plan instead.
  //
  // A `tier`/`interval` target names the tier outright; a `plan=<lookupKey>` one has to ask the
  // catalogue, which is the only thing that knows which price a key is - deliberately not parsed
  // out of the key itself. Deduped by SWR with the panel's own read, so it costs no extra request.
  const { cards } = usePlanCards();
  const tier = pendingPlan.tier ?? findPlanCard(cards, pendingPlan)?.tier ?? null;
  const tierName = tier ? tierLabel(tier) : null;
  // The iframe's accessible name, so a screen reader announces what is being bought.
  const title = tierName
    ? t('SETUP_SUBSCRIBE_TITLE_PLAIN_TIER', { defaultValue: 'Subscribe to {{tier}}', tier: tierName })
    : t('SETUP_SUBSCRIBE_TITLE_PLAIN', { defaultValue: 'Subscribe to unlock your plan' });

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            {/* Falls back to the untiered wording while the catalogue is still resolving a
                `plan=` lookup key - both read correctly, so the tier name only ever appears, it
                never replaces a sentence that was wrong. */}
            {tierName ? (
              <OnboardingTitle
                text="Set up your <span>{{tier}} plan</span>"
                translationKey="SETUP_SUBSCRIBE_TITLE_TIER"
                values={{ tier: tierName }}
              />
            ) : (
              <OnboardingTitle text="Set up your <span>plan</span>" translationKey="SETUP_SUBSCRIBE_TITLE_PLAN" />
            )}
            <P2GText variant="subtitle1">
              {t('SETUP_SUBSCRIBE_SUBTITLE', {
                defaultValue: "Finish setting up the plan you picked, or continue on Free if you'd rather."
              })}
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GContainer maxWidth="md">
          <P2GBox py={3}>
            <SubscribePanel plan={pendingPlan} title={title} onDone={onDone} />
          </P2GBox>
        </P2GContainer>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        skip={
          <P2GButton
            variant="text"
            onClick={() => {
              onDone(null);
            }}
          >
            {t('SKIP_CONTINUE_ON_BASE_PLAN_BUTTON', { defaultValue: 'Skip, continue on Free plan' })}
          </P2GButton>
        }
      >
        <P2GText variant="subtitle2">
          Finish setting up the plan you picked, or continue on Free if you'd rather.
        </P2GText>
      </OnboardingActionBar>
    </>
  );
};

export default SubscribeScreen;
